// src/pages/Auth/ResetPassword.jsx
// -----------------------------------------------------------------------------
// Reset password screen (Additional Requirement: POST /api/Auth/reset-password).
// Opened from the emailed link /reset-password?token=... ; sends the one-time
// token + new password. The backend rejects used or expired tokens.
// -----------------------------------------------------------------------------
import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../api/authService';
import afixLogo from '../../assets/images/afix-logo.png';
import './auth.css';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await authService.resetPassword(token, newPassword);
      // Backend signals failure with 200 OK + a plain string message.
      if (typeof res.data === 'string' && res.data !== 'Password reset successful') {
        throw new Error(res.data);
      }
      toast.success('Password reset completed successfully, please login.');
      navigate('/login');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Password reset failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-screen">
      {/* ---------- Left: brand panel ---------- */}
      <div className="auth-brand-panel">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />

        <div className="auth-brand-content">
          <img src={afixLogo} alt="AFix logo" className="auth-brand-logo" />
          <h1>Afix</h1>
          <p className="auth-tagline">Track &middot; Fix &middot; Deliver</p>
          <p className="auth-sub">
            Choose a new password to restore access to your account.
          </p>
        </div>
      </div>

      {/* ---------- Right: form panel ---------- */}
      <div className="auth-form-panel">
        <div className="auth-form-box">
          <img src={afixLogo} alt="AFix" className="auth-form-logo" />
          <h2>Reset Password</h2>
          <p className="subtitle">Enter your new password below</p>

          {error && <div className="auth-error">{error}</div>}

          {!token ? (
            <p>
              This reset link is invalid. Please request a new one from{' '}
              <Link to="/forgot-password">Forgot Password</Link>.
            </p>
          ) : (
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="auth-password-wrap">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
          )}

          <div className="form-links" style={{ marginTop: '20px', textAlign: 'center' }}>
            <Link to="/login">Back to Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
